import {
  DEFAULT_MAX_RESCHEDULES,
  LOCAL_VISIT_STATUS,
  RESCHEDULE_BUSINESS_DAYS,
} from 'constants/digitalLoanPhase3';
import {
  buildAvailableVisitDates,
  DEFAULT_VISIT_TIME_SLOTS,
  resolveScheduledVisitAt,
} from 'modules/digitalLoan/visitSchedule';

const PAYMENT_FREQUENCY_LABEL = Object.freeze({
  MENSUAL: 'Mensual',
  CATORCENAL: 'Catorcenal',
  MONTHLY: 'Mensual',
  BIWEEKLY: 'Catorcenal',
  Mensual: 'Mensual',
  Catorcenal: 'Catorcenal',
});

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function firstNumber(...values) {
  const found = values.find((value) => {
    if (value === undefined || value === null || value === '') return false;
    return Number.isFinite(Number(value));
  });
  return found === undefined ? null : Number(found);
}

export function formatPaymentFrequencyLabel(value) {
  if (!value) return '';
  return PAYMENT_FREQUENCY_LABEL[value] || String(value);
}

export function buildGuaranteeLabel(guarantee = {}, snapshot = {}) {
  const fullDescription = String(snapshot.guaranteeDescription || '').trim();
  if (fullDescription) return fullDescription;
  const parts = [
    guarantee.articleName,
    guarantee.brandName,
    guarantee.modelName,
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean);
  if (parts.length) return parts.join(' ');
  return snapshot.guaranteeName || '';
}

/**
 * TODO: replace inferred visit status with explicit
 * visitStatus/canReschedule/canCancel if Backend adds those fields.
 */
export function inferVisitStatus(status, snapshot) {
  const app = status?.activeApplication || {};
  const gestoria = status?.gestoria || {};
  const snapshotCancelled = (
    snapshot?.localVisitStatus === LOCAL_VISIT_STATUS.CANCELLED
  );

  if (snapshotCancelled) {
    const backendChangedStep = (
      app.currentStep
      && snapshot.currentStep
      && app.currentStep !== snapshot.currentStep
    );
    const backendChangedStatus = (
      app.status
      && snapshot.status
      && app.status !== snapshot.status
    );
    const backendNewVisit = (
      gestoria.scheduledVisitAt
      && snapshot.scheduledVisitAt
      && gestoria.scheduledVisitAt !== snapshot.scheduledVisitAt
    );
    if (!backendChangedStep && !backendChangedStatus && !backendNewVisit) {
      return LOCAL_VISIT_STATUS.CANCELLED;
    }
  }

  if (firstNumber(gestoria.rescheduleCount, snapshot?.rescheduleCount) > 0) {
    return LOCAL_VISIT_STATUS.RESCHEDULED;
  }
  if (gestoria.scheduledVisitAt || snapshot?.scheduledVisitAt) {
    return LOCAL_VISIT_STATUS.SCHEDULED;
  }
  return snapshot?.localVisitStatus || null;
}

/**
 * Normaliza GET /digital-loan/status + snapshot fallback.
 * Prioridad: status → respuestas de schedule/preapproval → snapshot.
 */
export function normalizePhase3Status(statusResponse, snapshot = null) {
  const status = statusResponse || {};
  const app = status.activeApplication || {};
  const questionnaire = status.questionnaire || {};
  const guarantee = status.guarantee || {};
  const valuation = status.valuation || {};
  const gestoria = status.gestoria || {};
  const fallback = snapshot || {};

  const maxFromBackend = firstNumber(
    gestoria.maxReschedules,
    fallback.maxReschedules,
  );
  // TODO: usar maxReschedules de Backend cuando siempre venga en status.
  const maxReschedules = maxFromBackend ?? DEFAULT_MAX_RESCHEDULES;

  const scheduledVisitAt = resolveScheduledVisitAt(
    gestoria.scheduledVisitAt,
    fallback.scheduledVisitAt,
  );

  return {
    applicationId: firstDefined(
      app.applicationId,
      fallback.applicationId,
    ) || null,
    applicationNumber: app.applicationNumber || null,
    status: firstDefined(app.status, fallback.status) || null,
    currentPhase: firstDefined(app.currentPhase, fallback.currentPhase) || null,
    currentStep: firstDefined(app.currentStep, fallback.currentStep) || null,
    isException: app.isException === true,
    rejectionReason: app.rejectionReason || null,
    requestedAmount: firstNumber(
      questionnaire.requestedAmount,
      valuation.clientRequestedAmount,
      fallback.requestedAmount,
    ),
    requestedTerm: firstNumber(
      questionnaire.requestedTerm,
      valuation.clientRequestedTerm,
      fallback.requestedTerm,
    ),
    paymentFrequency: formatPaymentFrequencyLabel(
      firstDefined(
        questionnaire.paymentFrequency,
        fallback.paymentFrequency,
      ),
    ),
    proximityType: questionnaire.proximityType || null,
    familyName: guarantee.familyName || null,
    articleName: guarantee.articleName || null,
    brandName: guarantee.brandName || null,
    modelName: guarantee.modelName || null,
    conservationState: guarantee.conservationState || null,
    customerDeclaredValue: firstNumber(guarantee.customerDeclaredValue),
    photosCount: firstNumber(guarantee.photosCount),
    photoMatchPassed: guarantee.photoMatchPassed,
    amountSource: valuation.amountSource || null,
    finalMarketValue: firstNumber(valuation.finalMarketValue),
    appraisalAmount: firstNumber(
      valuation.appraisalAmount,
      fallback.appraisalAmount,
    ),
    maxLoanAmount: firstNumber(
      valuation.maxLoanAmount,
      fallback.maxLoanAmount,
    ),
    cappedByMaxAmount: typeof valuation.cappedByMaxAmount === 'boolean'
      ? valuation.cappedByMaxAmount
      : fallback.cappedByMaxAmount === true,
    suggestedLoanAmount: firstNumber(
      valuation.suggestedLoanAmount,
      fallback.suggestedLoanAmount,
    ),
    riskLevel: valuation.riskLevel || fallback.riskLevel || null,
    riskLevelLabel: valuation.riskLevelLabel || fallback.riskLevelLabel || null,
    interestRate: firstNumber(valuation.interestRate, fallback.interestRate),
    express: valuation.express === true || fallback.express === true,
    preapprovalAmount: firstNumber(
      gestoria.preapprovalAmount,
      fallback.acceptedAmount,
    ),
    acceptedAmount: firstNumber(
      gestoria.preapprovalAmount,
      valuation.suggestedLoanAmount,
      fallback.acceptedAmount,
      fallback.suggestedLoanAmount,
    ),
    preapprovalAcceptedAt: gestoria.preapprovalAcceptedAt || null,
    scheduledVisitAt,
    rescheduleCount: firstNumber(
      gestoria.rescheduleCount,
      fallback.rescheduleCount,
    ) || 0,
    maxReschedules,
    dispatchedAt: gestoria.dispatchedAt || null,
    visitExecutedAt: gestoria.visitExecutedAt || null,
    backofficeDecision: gestoria.backofficeDecision || null,
    gestoriaCompletedAt: gestoria.completedAt || null,
    // TODO: remove snapshot fallback for guarantee image once Backend
    // adds guaranteeImageUrl or photo URL.
    guaranteeImage: fallback.guaranteeImage || fallback.guaranteeImageUrl || null,
    guaranteeDescription: firstDefined(fallback.guaranteeDescription) || null,
    guaranteeName: buildGuaranteeLabel(guarantee, fallback),
    paymentDay: fallback.paymentDay || '',
    localVisitStatus: inferVisitStatus(status, fallback),
  };
}

export function buildVisitCalendar() {
  // TODO: Replace frontend-generated slots with backend/provider
  // availability if Backend exposes a slot calendar later.
  return {
    availableVisitDates: buildAvailableVisitDates(
      new Date(),
      RESCHEDULE_BUSINESS_DAYS,
    ),
    availableVisitTimeSlots: [...DEFAULT_VISIT_TIME_SLOTS],
  };
}

export function mergePreOfferDisplay(preOffer, display) {
  const calendar = buildVisitCalendar();
  return {
    amount: firstNumber(
      preOffer?.amount,
      display?.preapprovalAmount,
      display?.acceptedAmount,
      display?.suggestedLoanAmount,
    ),
    term: firstDefined(preOffer?.term, display?.requestedTerm) ?? null,
    paymentFrequency: firstDefined(
      preOffer?.paymentFrequency,
      display?.paymentFrequency,
    ) || '',
    paymentDay: firstDefined(preOffer?.paymentDay, display?.paymentDay) || '',
    guaranteeName: firstDefined(
      display?.guaranteeDescription,
      preOffer?.guaranteeName,
      display?.guaranteeName,
    ) || '',
    guaranteeImage: preOffer?.useGuaranteeImage
      ? (preOffer.guaranteeImage || null)
      : (firstDefined(
        preOffer?.guaranteeImage,
        display?.guaranteeImage,
      ) || null),
    riskLevelLabel: firstDefined(
      preOffer?.riskLevelLabel,
      display?.riskLevelLabel,
    ) || null,
    interestRate: firstNumber(preOffer?.interestRate, preOffer?.rate, display?.interestRate),
    express: preOffer?.express === true || display?.express === true,
    appraisalAmount: firstNumber(
      preOffer?.appraisalAmount,
      display?.appraisalAmount,
    ),
    maxLoanAmount: firstNumber(
      preOffer?.maxLoanAmount,
      display?.maxLoanAmount,
    ),
    cappedByMaxAmount: typeof preOffer?.cappedByMaxAmount === 'boolean'
      ? preOffer.cappedByMaxAmount
      : display?.cappedByMaxAmount === true,
    availableVisitDates: preOffer?.availableVisitDates?.length
      ? preOffer.availableVisitDates
      : calendar.availableVisitDates,
    availableVisitTimeSlots: preOffer?.availableVisitTimeSlots?.length
      ? preOffer.availableVisitTimeSlots
      : calendar.availableVisitTimeSlots,
  };
}

export function resolveMaxReschedules(display) {
  if (Number.isFinite(Number(display?.maxReschedules))) {
    return Number(display.maxReschedules);
  }
  // TODO: usar maxReschedules de Backend cuando siempre venga en status.
  return DEFAULT_MAX_RESCHEDULES;
}
